import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/api';
import './RestoreModal.css';

/**
 * RestoreModal - Modal for viewing and restoring soft-deleted items
 */
function RestoreModal({
  show,
  onClose,
  graphId,
  activeTab = 'nodes',
  onRestore,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [restoring, setRestoring] = useState(new Set());

  const loadDeletedItems = useCallback(async () => {
    if (!show || !graphId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      let response;
      if (activeTab === 'nodes' || activeTab === 'gates') {
        response = await apiService.listDeletedNodes(graphId, 1, 50);
        // Filter for gates if needed
        if (activeTab === 'gates') {
          const gateTypes = ['rule_gate', 'threshold_gate', 'and_gate', 'or_gate', 'not_gate', 'xor_gate'];
          response.nodes = (response.nodes || []).filter(n => gateTypes.includes(n.type));
        }
        setItems(response.nodes || []);
      } else if (activeTab === 'synapses') {
        response = await apiService.listDeletedSynapses(graphId, 1, 50);
        setItems(response.synapses || []);
      }
    } catch (err) {
      console.error('Error loading deleted items:', err);
      setError('Failed to load deleted items');
    } finally {
      setLoading(false);
    }
  }, [show, graphId, activeTab]);

  useEffect(() => {
    loadDeletedItems();
  }, [loadDeletedItems]);

  const handleRestore = async (id) => {
    setRestoring(prev => new Set(prev).add(id));
    
    try {
      const type = activeTab === 'gates' ? 'gate' : (activeTab === 'synapses' ? 'synapse' : 'node');
      await onRestore(type, id);
      // Remove from list
      setItems(prev => prev.filter(item => (item.id || item.nodeId) !== id));
    } catch (err) {
      console.error('Error restoring item:', err);
    } finally {
      setRestoring(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  if (!show) return null;

  const getItemKey = (item) => item.id || item.nodeId || item.synapseId;
  const getItemName = (item) => item.name || item.nodeName || item.description || item.edgeName || 'Unknown';
  const getItemType = (item) => item.type || item.nodeType || 'N/A';

  return (
    <div className="restore-modal-overlay" onClick={onClose}>
      <div className="restore-modal" onClick={(e) => e.stopPropagation()}>
        <div className="restore-modal-header">
          <h3>Restore Deleted {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}</h3>
          <button className="restore-modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="restore-modal-body">
          {loading && (
            <div className="restore-loading">Loading deleted items...</div>
          )}
          
          {error && (
            <div className="restore-error">{error}</div>
          )}
          
          {!loading && !error && items.length === 0 && (
            <div className="restore-empty">
              No deleted {activeTab === 'synapses' ? 'connections' : activeTab} found.
            </div>
          )}
          
          {!loading && !error && items.length > 0 && (
            <div className="restore-list">
              <table className="restore-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Deleted</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const id = getItemKey(item);
                    const isRestoring = restoring.has(id);
                    
                    return (
                      <tr key={id}>
                        <td>{getItemName(item)}</td>
                        <td>{getItemType(item)}</td>
                        <td>
                          {item.deletedAt 
                            ? new Date(item.deletedAt).toLocaleDateString() 
                            : 'Unknown'}
                        </td>
                        <td>
                          <button
                            className="btn-restore"
                            onClick={() => handleRestore(id)}
                            disabled={isRestoring}
                          >
                            {isRestoring ? 'Restoring...' : 'Restore'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        
        <div className="restore-modal-footer">
          <button className="btn-close" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default RestoreModal;
